<template>
  <div class="fridge-order-calendar" id="printable-order-calendar">
    <div class="fridge-order-calendar__toolbar non-printable">
      <button type="button" class="fridge-order-calendar__btn" @click="done()">Back</button>
      <button type="button" class="fridge-order-calendar__btn" @click="print()">Print</button>
      <button
        type="button"
        class="fridge-order-calendar__btn fridge-order-calendar__btn--pdf"
        :disabled="isPdfBusy"
        @click="savePdf()"
      >
        {{ isPdfBusy ? 'PDF…' : 'PDF' }}
      </button>
      <label class="fridge-order-calendar__toggle">
        <input v-model="isShowDescriptions" type="checkbox" />
        Show descriptions
      </label>
    </div>

    <header class="fridge-order-calendar__header">
      <div class="fridge-order-calendar__student">
        <p class="fridge-order-calendar__student-name">
          {{ displayName(student.firstName) }}&rsquo;s lunch orders
        </p>
        <p class="fridge-order-calendar__student-meta">
          {{ displayName(student.teacher) }} &middot; Grade {{ student.grade }}
        </p>
      </div>
      <h1 class="fridge-order-calendar__month">{{ monthTitle }}</h1>
      <img alt="mySchoolCafe" src="/img/myschoolCafe3.png" class="fridge-order-calendar__logo" />
    </header>

    <div class="fridge-order-calendar__scroll">
      <div class="fridge-order-calendar__grid" role="grid" :aria-label="monthTitle + ' order calendar'">
        <div
          v-for="(weekday, index) in weekdayLabels"
          :key="'dow-' + index"
          class="fridge-order-calendar__dow"
          :class="'fridge-order-calendar__dow--' + weekday.key"
          role="columnheader"
        >
          {{ weekday.label }}
        </div>

        <div
          v-for="cell in calendarCells"
          :key="cell.key"
          class="fridge-order-calendar__cell"
          :class="cellClass(cell)"
          role="gridcell"
          @click="onCellClick(cell)"
        >
          <div v-if="cell.dayOfMonth" class="fridge-order-calendar__cell-head">
            <span
              class="fridge-order-calendar__weekday-tag"
              :class="cell.weekdayKey ? 'fridge-order-calendar__weekday-tag--' + cell.weekdayKey : null"
            >
              {{ weekdayLabelFor(cell.weekdayKey) }}
            </span>
            <span
              class="fridge-order-calendar__date"
              :class="'fridge-order-calendar__date--' + cell.weekdayKey"
            >
              {{ cell.dayOfMonth }}
            </span>
          </div>
          <div
            v-if="cell.menuDay && (cell.menuDay.title || cell.menuDay.description)"
            class="fridge-order-calendar__day-theme"
            :class="cell.weekdayKey ? 'fridge-order-calendar__day-theme--' + cell.weekdayKey : null"
          >
            <span v-if="cell.menuDay.title" class="fridge-order-calendar__day-theme-title">
              {{ displayName(cell.menuDay.title) }}
            </span>
            <span
              v-if="cell.menuDay.description"
              class="fridge-order-calendar__day-theme-desc"
            >
              {{ displayName(cell.menuDay.description) }}
            </span>
          </div>
          <ul v-if="cell.order && cell.order.items" class="fridge-order-calendar__items">
            <li v-for="(item, itemIndex) in cell.order.items" :key="cell.key + '-item-' + itemIndex">
              <span class="fridge-order-calendar__item-title">
                {{ item.title }}<span v-if="item.qty != 1"> ({{ item.qty }})</span>
              </span>
              <span v-if="isShowDescriptions && item.description" class="fridge-order-calendar__item-desc">
                {{ item.description }}
              </span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <footer class="fridge-order-calendar__footer">
      <span>Order cutoff is 7:00 AM</span>
      <span class="fridge-order-calendar__footer-brand">mySchoolCafe</span>
    </footer>
  </div>
</template>

<script>
  import Moment from 'moment-timezone';
  import { apiMenuDayKey, pickerEventDate, tenantTz } from '@/utils/mealDates';
  import { toTitleCase } from '@/utils/displayText';
  import { calendarCellWeekdayName } from '@/utils/mealDayColors';
  import { buildOrderCalendarPdf } from '@/utils/calendarPdf';
  import { offerPdfDelivery, pdfDeliveryAlert, sanitizePdfFilename } from '@/utils/pdfDelivery';

  const WEEKDAY_LABELS = [
    { key: 'sunday', label: 'Sun' },
    { key: 'monday', label: 'Mon' },
    { key: 'tuesday', label: 'Tue' },
    { key: 'wednesday', label: 'Wed' },
    { key: 'thursday', label: 'Thu' },
    { key: 'friday', label: 'Fri' },
    { key: 'saturday', label: 'Sat' },
  ];

  export default {
    name: 'OrderCalendarCard',
    props: {
      orders: { type: Array, default: () => [] },
      student: { type: Object, default: () => ({}) },
      calendar: { type: Object, default: null },
      viewDate: { type: [Date, Object, String], default: null },
    },
    data() {
      return {
        isShowDescriptions: true,
        isPdfBusy: false,
        weekdayLabels: WEEKDAY_LABELS,
      };
    },
    mounted() {
      if (this.isMobileCalendarViewport()) {
        this.isShowDescriptions = false;
      }
    },
    computed: {
      tenantTimezone() {
        return tenantTz(this.$root.account);
      },
      monthAnchor() {
        const tz = this.tenantTimezone;
        const orders = Array.isArray(this.orders) ? this.orders : [];
        const firstOrder = orders.find((order) => order && order.orderDate);
        if (firstOrder) {
          return Moment(firstOrder.orderDate).tz(tz).add(12, 'hours').startOf('month');
        }
        if (this.viewDate) {
          const view = Moment(this.viewDate);
          if (view.isValid()) {
            return view.tz(tz).add(12, 'hours').startOf('month');
          }
        }
        if (this.calendar && Array.isArray(this.calendar.menu) && this.calendar.menu.length > 0) {
          const firstMenu = this.calendar.menu.find((menuDay) => menuDay && menuDay.menuDate);
          if (firstMenu) {
            return Moment(firstMenu.menuDate).tz(tz).add(12, 'hours').startOf('month');
          }
        }
        if (this.calendar && this.calendar.calendarStartDate) {
          return Moment(this.calendar.calendarStartDate).tz(tz).startOf('month');
        }
        return Moment().tz(tz).startOf('month');
      },
      monthTitle() {
        return this.monthAnchor.format('MMMM YYYY');
      },
      ordersByDayKey() {
        const tz = this.tenantTimezone;
        const map = {};
        const orders = Array.isArray(this.orders) ? this.orders : [];
        orders.forEach((order) => {
          if (!order || !order.orderDate) {
            return;
          }
          map[apiMenuDayKey(order.orderDate, tz)] = order;
        });
        return map;
      },
      menuByDayKey() {
        const tz = this.tenantTimezone;
        const map = {};
        if (!this.calendar || !Array.isArray(this.calendar.menu)) {
          return map;
        }
        this.calendar.menu.forEach((menuDay) => {
          if (!menuDay || !menuDay.menuDate) {
            return;
          }
          map[apiMenuDayKey(menuDay.menuDate, tz)] = menuDay;
        });
        return map;
      },
      calendarCells() {
        const tz = this.tenantTimezone;
        const year = this.monthAnchor.year();
        const month = this.monthAnchor.month() + 1;
        const daysInMonth = this.monthAnchor.daysInMonth();
        const leadEmpty = this.monthAnchor.day();
        const cells = [];

        for (let i = 0; i < leadEmpty; i++) {
          cells.push({
            key: `empty-${i}`,
            dayOfMonth: null,
            order: null,
            weekdayKey: null,
            date: null,
          });
        }

        for (let day = 1; day <= daysInMonth; day++) {
          const weekdayKey = calendarCellWeekdayName(year, month, day, tz).toLowerCase();
          const dayKey = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
          cells.push({
            key: dayKey,
            dayOfMonth: day,
            order: this.ordersByDayKey[dayKey] || null,
            menuDay: this.menuByDayKey[dayKey] || null,
            weekdayKey,
            date: pickerEventDate(year, month, day),
          });
        }

        return cells;
      },
    },
    methods: {
      displayName(value) {
        return toTitleCase(value);
      },
      isMobileCalendarViewport() {
        if (typeof window === 'undefined' || !window.matchMedia) {
          return false;
        }
        return window.matchMedia('(max-width: 991px)').matches;
      },
      weekdayLabelFor(weekdayKey) {
        if (!weekdayKey) {
          return '';
        }
        const match = WEEKDAY_LABELS.find((d) => d.key === weekdayKey);
        return match ? match.label : weekdayKey;
      },
      cellClass(cell) {
        if (!cell.dayOfMonth) {
          return 'fridge-order-calendar__cell--empty';
        }
        const classes = [`fridge-order-calendar__cell--${cell.weekdayKey}`];
        if (cell.order) {
          classes.push('fridge-order-calendar__cell--ordered');
        }
        if (!cell.order && !cell.menuDay) {
          classes.push('fridge-order-calendar__cell--sparse');
        }
        return classes;
      },
      onCellClick(cell) {
        if (!cell.date) {
          return;
        }
        this.$emit('click', cell.date);
      },
      done() {
        this.$emit('done');
      },
      print() {
        window.print();
      },
      async savePdf() {
        if (this.isPdfBusy) {
          return;
        }
        this.isPdfBusy = true;
        try {
          const blob = buildOrderCalendarPdf({
            monthTitle: this.monthTitle,
            studentFirstName: this.student.firstName,
            studentTeacher: this.student.teacher,
            studentGrade: this.student.grade,
            calendarCells: this.calendarCells,
            showDescriptions: this.isShowDescriptions,
            displayName: this.displayName,
          });
          const filename = sanitizePdfFilename(
            `${this.displayName(this.student.firstName)}-${this.monthTitle}-orders`
          );
          const result = await offerPdfDelivery(blob, `${filename}.pdf`, this.$swal);
          const alert = pdfDeliveryAlert(result);
          if (alert) {
            await this.$swal.fire(alert);
          }
        } catch (err) {
          await this.$swal.fire({
            icon: 'error',
            title: 'Could not create PDF',
            text: (err && err.message) ? err.message : 'Please try again.',
          });
        } finally {
          this.isPdfBusy = false;
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  @import '@/assets/sass/mealPalette';

  .fridge-order-calendar {
    background: #fff;
    border-radius: 0.75rem;
    padding: 1rem 1.15rem 1.1rem;
    box-shadow: 0 0.35rem 1.25rem rgba(0, 0, 0, 0.08);
  }

  .fridge-order-calendar__toolbar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    gap: 0.65rem 1rem;
    margin-bottom: 1rem;
  }

  .fridge-order-calendar__btn {
    min-width: 7rem;
    padding: 0.45rem 1.35rem;
    border: 2px solid $meal-chrome-dow;
    border-radius: 2rem;
    background: $meal-chrome-dow;
    color: #fff;
    font-size: 0.92rem;
    font-weight: 700;
    line-height: 1.2;
    cursor: pointer;
    transition: background-color 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover,
    &:focus {
      background: darken($meal-chrome-dow, 6%);
      border-color: darken($meal-chrome-dow, 6%);
      color: #fff;
      outline: none;
      box-shadow: 0 0 0 0.2rem rgba($meal-chrome-dow, 0.28);
    }

    &:active {
      background: darken($meal-chrome-dow, 10%);
      border-color: darken($meal-chrome-dow, 10%);
    }

    &:disabled {
      opacity: 0.65;
      cursor: wait;
    }

    &--pdf {
      background: $meal-brand;
      border-color: $meal-brand;

      &:hover,
      &:focus {
        background: darken($meal-brand, 6%);
        border-color: darken($meal-brand, 6%);
        box-shadow: 0 0 0 0.2rem rgba($meal-brand, 0.28);
      }

      &:active {
        background: darken($meal-brand, 10%);
        border-color: darken($meal-brand, 10%);
      }
    }
  }

  .fridge-order-calendar__toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    margin: 0;
    font-size: 0.92rem;
    font-weight: 600;
    color: $meal-chrome-dow;
    cursor: pointer;

    input {
      margin: 0;
    }
  }

  .fridge-order-calendar__header {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
    gap: 0.75rem 1rem;
    margin-bottom: 0.85rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid rgba($meal-brand, 0.25);
  }

  .fridge-order-calendar__student-name {
    margin: 0;
    font-size: 1.05rem;
    font-weight: 700;
    color: $meal-chrome-dow;
  }

  .fridge-order-calendar__student-meta {
    margin: 0.15rem 0 0;
    font-size: 0.88rem;
    color: #5f6d78;
  }

  .fridge-order-calendar__month {
    margin: 0;
    font-size: clamp(1.65rem, 3vw, 2.35rem);
    font-weight: 700;
    line-height: 1.1;
    text-align: center;
    color: $meal-brand;
    white-space: nowrap;
  }

  .fridge-order-calendar__logo {
    justify-self: end;
    max-width: 9.5rem;
    width: 100%;
    height: auto;
  }

  $fridge-calendar-cell-min: 6.25rem;

  .fridge-order-calendar__scroll {
    width: 100%;
    max-width: 100%;
  }

  .fridge-order-calendar__grid {
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    border: 1px solid rgba(0, 0, 0, 0.18);
    border-radius: 0.35rem;
    overflow: hidden;
  }

  .fridge-order-calendar__dow {
    padding: 0.4rem 0.2rem;
    text-align: center;
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
    color: #fff;
    border-right: 1px solid rgba(255, 255, 255, 0.25);
    border-bottom: 1px solid rgba(0, 0, 0, 0.12);

    &:last-child {
      border-right: none;
    }

    @each $name, $color in $meal-days {
      &--#{$name} {
        background: $color;
      }
    }
  }

  .fridge-order-calendar__cell {
    min-height: 5.75rem;
    padding: 0.3rem 0.35rem 0.45rem;
    border-right: 1px solid rgba(0, 0, 0, 0.1);
    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    cursor: default;

    &:nth-child(7n) {
      border-right: none;
    }

    &--empty {
      background: #f4f4f4;
    }

    &--ordered {
      cursor: pointer;
    }

    &--sunday,
    &--saturday {
      background: #fafafa;
    }
  }

  .fridge-order-calendar__date {
    display: inline-block;
    font-size: 1.35rem;
    font-weight: 700;
    line-height: 1;
    margin-bottom: 0.2rem;

    @each $name, $color in $meal-days {
      &--#{$name} {
        color: $color;
      }
    }
  }

  .fridge-order-calendar__cell-head {
    display: flex;
    align-items: baseline;
    gap: 0.45rem;
    margin-bottom: 0.2rem;
  }

  .fridge-order-calendar__weekday-tag {
    display: none;
    padding: 0.15rem 0.45rem;
    border-radius: 999px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #fff;
    line-height: 1.2;

    @each $name, $color in $meal-days {
      &--#{$name} {
        background: $color;
      }
    }
  }

  .fridge-order-calendar__day-theme {
    margin: 0.25rem 0 0;
    padding: 0.38rem 0.42rem 0.42rem;
    border-radius: 0.32rem;
    background: #fff;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.12);
    border-left: 3px solid $meal-chrome-dow;

    @each $name, $color in $meal-days {
      &--#{$name} {
        border-left-color: $color;
        background: linear-gradient(
          135deg,
          rgba($color, 0.16) 0%,
          rgba($color, 0.06) 100%
        );
      }
    }

    & + .fridge-order-calendar__items {
      margin-top: 0.45rem;
      padding-top: 0.42rem;
      border-top: 1px dashed rgba(0, 0, 0, 0.2);
    }
  }

  .fridge-order-calendar__day-theme-title {
    display: block;
    font-size: 0.74rem;
    font-weight: 800;
    line-height: 1.25;
    letter-spacing: 0.02em;
    color: #1a2430;
    text-align: left;
  }

  .fridge-order-calendar__day-theme-desc {
    display: block;
    margin-top: 0.18rem;
    font-size: 0.66rem;
    font-weight: 500;
    line-height: 1.35;
    color: #334155;
    white-space: pre-wrap;
    text-align: left;
  }

  .fridge-order-calendar__items {
    list-style: none;
    margin: 0;
    padding: 0;

    li + li {
      margin-top: 0.25rem;
      padding-top: 0.25rem;
      border-top: 1px dashed rgba(0, 0, 0, 0.12);
    }
  }

  .fridge-order-calendar__item-title {
    display: block;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.25;
    color: #2a3340;
    text-align: left;
  }

  .fridge-order-calendar__item-desc {
    display: block;
    margin-top: 0.1rem;
    font-size: 0.64rem;
    line-height: 1.25;
    color: #6b7280;
    white-space: pre-wrap;
    text-align: left;
    padding-left: 1em;
  }

  .fridge-order-calendar__footer {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-top: 0.65rem;
    font-size: 0.82rem;
    color: #5f6d78;
  }

  .fridge-order-calendar__footer-brand {
    font-weight: 700;
    color: $meal-brand;
  }

  @media (max-width: 991px) {
    .fridge-order-calendar {
      padding: 0.65rem 0.45rem 0.85rem;
      border-radius: 0;
    }

    .fridge-order-calendar__header {
      grid-template-columns: 1fr;
      text-align: center;
      margin-bottom: 0.65rem;
      padding-bottom: 0.55rem;
    }

    .fridge-order-calendar__logo {
      justify-self: center;
      max-width: 7rem;
    }

    .fridge-order-calendar__month {
      white-space: normal;
    }

    .fridge-order-calendar__scroll {
      overflow: auto;
      -webkit-overflow-scrolling: touch;
      margin: 0 -0.15rem;
      padding: 0 0.15rem 0.15rem;
    }

    .fridge-order-calendar__grid {
      grid-template-columns: repeat(7, minmax($fridge-calendar-cell-min, 1fr));
      min-width: calc(7 * #{$fridge-calendar-cell-min});
      width: max-content;
      max-width: none;
    }

    .fridge-order-calendar__cell {
      min-height: 5.75rem;
      min-width: $fridge-calendar-cell-min;
    }
  }

  @media (max-width: 767px) {
    .fridge-order-calendar__toolbar {
      gap: 0.5rem;
    }

    .fridge-order-calendar__btn {
      min-width: 6rem;
      padding: 0.4rem 1rem;
    }
  }

  @media print {
    @page {
      size: letter landscape;
      margin: 0.3in;
    }

    .fridge-order-calendar {
      box-shadow: none;
      border-radius: 0;
      padding: 0;
      width: 100%;
    }

    .non-printable {
      display: none !important;
    }

    .fridge-order-calendar__header {
      margin-bottom: 0.45rem;
      padding-bottom: 0.4rem;
    }

    .fridge-order-calendar__scroll {
      overflow: visible;
      margin: 0;
      padding: 0;
    }

    .fridge-order-calendar__grid {
      min-width: 0;
      width: 100%;
    }

    .fridge-order-calendar__month {
      font-size: 1.65rem;
    }

    .fridge-order-calendar__logo {
      max-width: 7rem;
    }

    .fridge-order-calendar__cell {
      min-height: 0.95in;
      break-inside: avoid;
      page-break-inside: avoid;
    }

    .fridge-order-calendar__dow,
    .fridge-order-calendar__cell--ordered,
    .fridge-order-calendar__day-theme {
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
</style>
